<template>
    <div class="tw:flex tw:flex-col tw:gap-4">
        <div v-if="canShowGeneratePodcast" class="tw:w-full podcast-config-panel">
            <podcast-configuration-summary
                class="tw:mb-3"
                :selected-count="selectedConfigCount"
                :readonly="!canEditPodcastConfiguration"
                @configure="isConfigDrawerOpen = true"
            />
            <e-field label="Delivery Instructions">
                <e-input
                    :model-value="generationContext"
                    type="textarea"
                    rows="4"
                    class="tw:w-full"
                    :disabled="!canEditPodcastConfiguration"
                    @update:modelValue="generationContext = $event"
                />
            </e-field>
        </div>

        <podcast-configuration-drawer
            :open="isConfigDrawerOpen"
            :readonly="!canEditPodcastConfiguration"
            :model-value="podcastConfig"
            @update:open="isConfigDrawerOpen = $event"
            @update:modelValue="podcastConfig = $event"
            @apply="handleConfigApply"
        />

        <div v-if="canShowGeneratePodcast" class="tw:w-full">
            <e-field label="Podcast Generation">
                <div class="tw:mb-6">
                    <e-button type="is-primary" :loading="isGenerating" @click="generatePodcast">
                        Generate Podcast
                    </e-button>
                </div>
                <p class="tw:ml-4">
                    Status: <strong>{{ generationStatusLabel }}</strong>
                </p>
                <p v-if="generationError" class="tw:mt-1 tw:text-red-600">{{ generationError }}</p>
            </e-field>
        </div>

        <div v-if="showPendingOrGeneratingMessage" class="tw:w-full">
            <e-message
                type="is-warning"
                :closable="false"
            >
                This podcast is being generated and is not available yet. Please check back in a few minutes.
            </e-message>
        </div>

        <div v-if="generationStatus === 'failed'" class="tw:w-full">
            <e-message
                type="is-danger"
                :closable="false"
            >
                {{ failedGenerationMessage }}
            </e-message>
        </div>

        <div v-if="showCompletedWithoutAudioMessage" class="tw:w-full">
            <e-message
                type="is-info"
                :closable="false"
            >
                Podcast generation is complete, but the audio URL is not available yet. Please refresh in a moment.
            </e-message>
        </div>

        <div v-if="isPodcastReady" class="tw:w-full">
            <div class="podcast-player-wrap">
                <audio-player
                    :src="audioUrl"
                    :initial-position="lastPosition"
                    @timeupdate="onTimeUpdate"
                    @ended="onEnded"
                />
                <p class="tw:mt-2">
                    Listen progress: <strong>{{ listenPercentage }}%</strong>
                </p>
            </div>
        </div>

        <div v-if="isPodcastReady && podcastScript" class="tw:w-full podcast-script-block">
            <e-collapse v-model:open="scriptAccordionOpen">
                <template #trigger="{ open, toggle }">
                    <div
                        class="podcast-script-trigger"
                        role="button"
                        tabindex="0"
                        @click.stop="toggle"
                        @keydown.enter.prevent="toggle"
                        @keydown.space.prevent="toggle"
                    >
                        <span class="tw:font-medium tw:text-gray-800">Podcast transcript</span>
                        <i :class="open ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="tw:text-gray-500 tw:transition-transform" aria-hidden="true" />
                    </div>
                </template>
                <div class="podcast-script-content">{{ podcastScript }}</div>
            </e-collapse>
        </div>

        <div v-if="isPodcastReady || canShowGeneratePodcast" class="tw:w-full">
            <e-field>
                <display-content :content="activityContent.content" />
            </e-field>
        </div>

        <div v-if="isPodcastReady && (userHasAuthorizedAttempt || (!completed && !readOnly))" class="tw:w-full">
            <div class="tw:max-w-xs">
                <e-button :loading="isLoading" :disabled="disabled" type="is-primary" @click="submit">
                    {{ translate('mark_complete') }}
                </e-button>
            </div>
        </div>

        <div v-if="!readOnly && canViewSubmissions" class="tw:w-full">
            <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading" />
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import AudioPlayer from '@/components/audio/AudioPlayer.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import PodcastConfigurationDrawer from '@/components/podcast-configuration/PodcastConfigurationDrawer.vue';
import PodcastConfigurationSummary from '@/components/podcast-configuration/PodcastConfigurationSummary.vue';
import ECollapse from '@/components/ui/ECollapse.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'CoursePodcastActivity',

    components: {
        AudioPlayer,
        DisplayContent,
        ECollapse,
        PodcastConfigurationDrawer,
        PodcastConfigurationSummary,
        Submissions,
    },

    mixins: [IsLateMixin, CourseActivityMixin, AttemptAuthorizationMixin],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        isConfigDrawerOpen: false,
        isGenerating: false,
        generationStatus: 'pending',
        generationError: null,
        generationStartedAt: null,
        generationCompletedAt: null,
        podcastScript: null,
        scriptAccordionOpen: false,
        audioUrl: null,
        listenPercentage: 0,
        lastPosition: 0,
        totalListenTimeSeconds: 0,
        lastSyncedAtSecond: 0,
        statusPollIntervalId: null,
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        isPodcastReady() {
            return this.generationStatus === 'completed' && !!this.audioUrl;
        },
        generationContext: {
            get() {
                return this.activityContent?.generation_context || '';
            },
            set(value) {
                this.$store.dispatch(`${this.namespace}/addActivityContent`, {
                    uuid: this.uuid,
                    field: 'generation_context',
                    value,
                });
            },
        },
        podcastConfig: {
            get() {
                return this.activityContent?.podcast_config || { selections: {} };
            },
            set(value) {
                this.$store.dispatch(`${this.namespace}/addActivityContent`, {
                    uuid: this.uuid,
                    field: 'podcast_config',
                    value,
                });
            },
        },
        selectedConfigCount() {
            const selections = this.podcastConfig?.selections || {};
            return Object.values(selections).reduce((count, subcategories) => {
                if (!subcategories || typeof subcategories !== 'object') return count;
                return (
                    count +
                    Object.values(subcategories).reduce((subCount, selected) => {
                        if (Array.isArray(selected)) return subCount + selected.length;
                        if (typeof selected === 'string' && selected !== '') return subCount + 1;
                        return subCount;
                    }, 0)
                );
            }, 0);
        },
        canEditPodcastConfiguration() {
            return ['pending', 'failed'].includes(this.generationStatus);
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: {},
            };
        },

        podcastSubmission() {
            return this.userSubmissions[0] || null;
        },

        canShowGeneratePodcast() {
            if (this.readOnly) return false;
            if (!this.course) return false;
            return this.authUser?.is_admin || this.$gate.allows('update-course-info', this.course);
        },

        isTemplateNamespace() {
            return this.namespace === 'ActivityTemplates';
        },
        podcastActivityId() {
            // Prefer the polymorphic content id payload, but fall back to activity contentable id.
            return (
                this.activityContent?.id ||
                this.activityContent?.contentable_id ||
                this.activity?.contentable_id ||
                this.get('contentable_id') ||
                null
            );
        },

        podcastStatusUrl() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return null;

            if (this.isTemplateNamespace) {
                return `/api/podcasts/template/${podcastActivityId}/status`;
            }

            return `/api/podcasts/${podcastActivityId}/status`;
        },

        podcastAudioUrlEndpoint() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return null;

            if (this.isTemplateNamespace) {
                return `/api/podcasts/template/${podcastActivityId}/audio-url`;
            }

            return `/api/podcasts/${podcastActivityId}/audio-url`;
        },

        showPendingOrGeneratingMessage() {
            return ['pending', 'generating'].includes(this.generationStatus);
        },

        showCompletedWithoutAudioMessage() {
            return this.generationStatus === 'completed' && !this.audioUrl;
        },

        isServiceUnavailableError() {
            const message = (this.generationError || '').toLowerCase();

            return message.includes('contact support') || message.includes('service is not available right now');
        },

        failedGenerationMessage() {
            if (this.isServiceUnavailableError) {
                return 'Contact support, this service is not available right now.';
            }

            return 'The podcast could not be generated yet. Please try generating again.';
        },

        generationStatusLabel() {
            if (this.generationStatus === 'pending') return 'To be generated';
            if (this.generationStatus === 'generating' && this.generationStartedAt) {
                const date = this.$moment.utc(this.generationStartedAt).tz(this.$timezone.name).format('LLL');
                return `Generation started on ${date}`;
            }
            if (this.generationStatus === 'generating') return 'Generation started';
            if (this.generationStatus === 'completed' && this.generationCompletedAt) {
                const date = this.$moment.utc(this.generationCompletedAt).tz(this.$timezone.name).format('LLL');
                return `Generated on ${date}`;
            }
            if (this.generationStatus === 'completed') return 'Generated';
            if (this.generationStatus === 'failed') return 'Generation failed';
            return 'To be generated';
        },
    },

    async created() {
        this.syncFromActivityContent();
        await this.loadLatestStatus();
        await this.fetchAudioUrlIfAvailable();
        this.ensureStatusPolling();
    },

    beforeUnmount() {
        this.clearStatusPolling();
    },

    methods: {
        async loadLatestStatus() {
            const statusUrl = this.podcastStatusUrl;
            if (!statusUrl) {
                this.syncFromActivityContent();
                return;
            }

            try {
                const { data } = await axios.get(statusUrl);
                this.generationStatus = data.generation_status || 'pending';
                this.generationError = data.generation_error || null;
                if (data?.generation_started_at != null) this.generationStartedAt = data.generation_started_at;
                if (data?.generation_completed_at != null) this.generationCompletedAt = data.generation_completed_at;
                if (data?.script != null) this.podcastScript = data.script;
            } catch (error) {
                this.syncFromActivityContent();
            }
        },

        syncFromActivityContent() {
            const content = this.activityContent || {};
            this.generationStatus = content.generation_status || 'pending';
            this.generationError = content.generation_error || null;
            this.generationStartedAt = content.generation_started_at ?? null;
            this.generationCompletedAt = content.generation_completed_at ?? null;
            this.podcastScript = content.script ?? null;
        },

        async generatePodcast() {
            const { podcastActivityId } = this;
            if (!podcastActivityId) return;

            this.isGenerating = true;
            this.generationError = null;

            try {
                await axios.post(`/api/podcasts/${podcastActivityId}/generate`);
                this.generationStatus = 'pending';
                this.ensureStatusPolling(true);
            } catch (error) {
                this.generationError = error?.response?.data?.message || 'Unable to queue podcast generation.';
            } finally {
                this.isGenerating = false;
            }
        },

        ensureStatusPolling(forceRestart = false) {
            const shouldPoll =
                ['pending', 'generating'].includes(this.generationStatus) ||
                (this.generationStatus === 'completed' && !this.audioUrl);
            if (!shouldPoll) {
                this.clearStatusPolling();
                return;
            }

            if (this.statusPollIntervalId && !forceRestart) {
                return;
            }

            this.clearStatusPolling();
            this.statusPollIntervalId = setInterval(() => {
                this.pollStatus();
            }, 15000);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },

        async pollStatus() {
            const statusUrl = this.podcastStatusUrl;
            if (!statusUrl) return;

            try {
                const { data } = await axios.get(statusUrl);
                this.generationStatus = data.generation_status || 'pending';
                this.generationError = data.generation_error || null;
                if (data?.generation_started_at != null) this.generationStartedAt = data.generation_started_at;
                if (data?.generation_completed_at != null) this.generationCompletedAt = data.generation_completed_at;
                if (data?.script != null) this.podcastScript = data.script;

                if (this.generationStatus === 'completed') {
                    await this.fetchAudioUrlIfAvailable();

                    if (this.audioUrl) {
                        this.clearStatusPolling();
                    }
                }

                if (this.generationStatus === 'failed') {
                    this.clearStatusPolling();
                }
            } catch (error) {
                // Keep polling on transient issues and retry URL retrieval if generation is complete.
                if (this.generationStatus === 'completed' && !this.audioUrl) {
                    await this.fetchAudioUrlIfAvailable();
                }
            }
        },

        async fetchAudioUrlIfAvailable() {
            if (!['completed'].includes(this.generationStatus)) return;
            const audioUrlEndpoint = this.podcastAudioUrlEndpoint;
            if (!audioUrlEndpoint) return;

            try {
                const { data } = await axios.get(audioUrlEndpoint);
                this.audioUrl = data?.url || null;
            } catch (error) {
                this.audioUrl = null;
            }

            if (this.podcastSubmission?.submittable) {
                this.listenPercentage = this.podcastSubmission.submittable.listen_percentage || 0;
                this.lastPosition = this.podcastSubmission.submittable.last_position_seconds || 0;
                this.totalListenTimeSeconds = this.podcastSubmission.submittable.total_listen_time_seconds || 0;
            }
        },

        async onTimeUpdate({ currentTime, duration }) {
            if (!duration) return;

            const previousLastPosition = this.lastPosition;
            this.lastPosition = Math.floor(currentTime);
            this.listenPercentage = Math.min(100, Math.floor((currentTime / duration) * 100));

            const forwardDelta = this.lastPosition - previousLastPosition;
            if (forwardDelta > 0) {
                this.totalListenTimeSeconds += forwardDelta;
            }

            if (this.lastPosition - this.lastSyncedAtSecond < 15) {
                return;
            }

            this.lastSyncedAtSecond = this.lastPosition;
            await this.syncPlaybackProgress();
        },

        async onEnded() {
            this.listenPercentage = 100;
            await this.syncPlaybackProgress();
        },

        async syncPlaybackProgress() {
            try {
                const { data } = await axios.put(`/api/podcasts/${this.podcastActivityId}/playback`, {
                    last_position_seconds: this.lastPosition,
                    furthest_position_seconds: this.lastPosition,
                    listen_percentage: this.listenPercentage,
                    total_listen_time_seconds: this.totalListenTimeSeconds,
                });

                if (data?.data?.marked_completed_at) {
                    this.$store.commit(`Activities/${this.uuid}/EDIT_FIELD`, {
                        field: 'completed',
                        value: data.data.marked_completed_at,
                    });
                }
            } catch (error) {
                // Keep the user flow uninterrupted if telemetry sync fails.
            }
        },
        handleConfigApply(payload) {
            if (!payload) return;
            this.podcastConfig = payload.config;
            this.generationContext = payload.text;
        },
    },
};
</script>

<style scoped>
.podcast-player-wrap {
    width: 100%;
    max-width: 700px;
}

.podcast-config-panel {
    width: 100%;
    max-width: 50vw;
}

.podcast-script-block :deep(.e-collapse-accordion) {
    width: 100%;
    max-width: 450px;
    min-width: 0;
    --p-accordion-header-padding: 0;
    --p-accordion-content-padding: 0;
}

.podcast-script-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    border: 1px solid var(--p-content-border-color, #e5e7eb);
    border-radius: 0.375rem;
    background: var(--p-content-surface-ground, #f9fafb);
    transition: background-color 0.15s ease;
}

.podcast-script-trigger:hover {
    background: var(--p-content-surface-hover, #f3f4f6);
}

.podcast-script-trigger:focus-visible {
    outline: 2px solid var(--p-focus-ring-color, #3b82f6);
    outline-offset: 2px;
}

.podcast-script-content {
    width: 100%;
    max-width: 100%;
    margin-top: 0.5rem;
    padding: 1rem 1.25rem;
    font-size: 0.875rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;
    border: 1px solid var(--p-content-border-color, #e5e7eb);
    border-radius: 0.375rem;
    background: #fff;
    max-height: 20rem;
    overflow-y: auto;
}
</style>
